import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, BehaviorSubject, throwError } from 'rxjs';
import { ConversationId } from '../../libraries/conversation-id/conversation-id';
import { RequestExecutor } from '../../libraries/request-executor/request-executor';
import { Constants } from '../../constants';
import { EntitlementModel } from '../../models/entitlement.model';
import { environment } from '../../../../environments/environment';
import { catchError } from 'rxjs/operators';
import {LocaleHandler} from '../../libraries/locale-handler/locale-handler';
import { ProfileService } from '../profile/profile.service';
import { ConfigService } from '../config/config.service';

@Injectable({
  providedIn: 'root'
})
export class TmsApiService extends RequestExecutor {

  /**
   * Array of Entitlements already validated from TMS
   * @var {Array<EntitlementModel>}
   */
  public validEntitlements: Array<EntitlementModel> = [];

  /**
   * Subject to manipulate the changes over the validEntitlements array
   * @var {Subject<EntitlementModel[]>}
   */
  public validEntitlementsSubject = new Subject<EntitlementModel[]>();

  /**
   * Array of Entitlements already validated from TMS
   * @TODO review
   * @var {Array<EntitlementModel>}
   */
  public confirmedEntitlementsSubject = new BehaviorSubject<EntitlementModel[]>(null);

  /**
   * Subject to manipulate the errors returned on TMS calls
   * @var {BehaviorSubject<string>}
   */
  public generalTmsErrorSubject = new BehaviorSubject<string>(null);

  /**
   * Class constructor
   *
   * @param _conversationId
   * @param _httpClient Local instance of HTTP Client
   * @param _localeHandler
   */
  constructor(
    protected _conversationId: ConversationId,
    protected _httpClient: HttpClient,
    private _localeHandler: LocaleHandler,
    private _profileServce: ProfileService,
    private _configService: ConfigService
  ) {
    super(_conversationId, _httpClient);
  }

  /**
   * Validate if Visual ID can be claimed
   *
   * @param visualId
   *
   * @returns {Observable<any>}
   */
  public validateVisualIdWithParameter(visualId: string): Observable<any> {
    return this.setRequestHeaders(this._getRequestHeaders())
      .setRequestBody({
          'visualId': visualId
      })
      .executeRequest(
        Constants.REQUEST_TYPE_POST,
        `${environment.apiConfiguration.basePath}${environment.apiConfiguration.paths.validate}`
      );
  }

  /**
   * Add a new entitlement into the validated entitlements array
   *
   * @param entitlements
   *
   * @returns {void}
   */
  public setValidEntitlements(entitlements: EntitlementModel[]): void {
    this.validEntitlements = entitlements;
    this.validEntitlementsSubject.next(this.validEntitlements);
  }

  /**
   * Gets the current valid entitlements
   *
   * @returns {EntitlementModel[]}
   */
  public getEntitlements(): EntitlementModel[] {
    return this.validEntitlements.slice();
  }

  /**
   * Add entitlements to keep tracking of the retrieved TMS data
   *
   * @param response
   * @param visualIdIndex
   *
   * @returns {void}
   */
  public addEntitlement(response: any, visualIdIndex: number): void {
    let entitlement: EntitlementModel;
    const guest = {
      'nickname': '',
      'firstName': '',
      'lastName': ''
    };

    if (!!response.assignedGuest) {
      guest.nickname = response.assignedGuest.nickname;
      guest.firstName = response.assignedGuest.firstName;
      guest.lastName = response.assignedGuest.lastName;
    }

    entitlement = new EntitlementModel(
        response.entitlementId,
        response.productName,
        response.guestAgeGroup,
        response.startDateTime,
        response.endDateTime,
        response.linked,
        response.shared,
        response.parkHopper,
        response.category,
        response.supplements,
        guest
    );

    if (!!this.validEntitlements[visualIdIndex]) {
      this.validEntitlements[visualIdIndex] = entitlement;
    } else {
      this.validEntitlements.push(entitlement);
    }

    this.validEntitlementsSubject.next(this.validEntitlements);
  }

  /**
   * Remove validated VID if the user entered a new invalid one in the same input
   *
   * @param visualIdIndex
   *
   * @returns {void}
   */
  public removePreviousValidVid(visualIdIndex: number): void {
    if (!!this.validEntitlements[visualIdIndex]) {
      this.validEntitlements.splice(visualIdIndex, 1);
    }

    this.validEntitlementsSubject.next(this.validEntitlements);
  }

  /**
   * @TODO Review this
   *
   * Clear valid entitlements
   *
   * @returns {void}
   */
  public clearValidatedEntitlements(): void {
    this.validEntitlements = [];
    this.validEntitlementsSubject.next(this.validEntitlements);
  }

  /**
   * @TODO Review edge case scnearios
   * Prepare the data to confirm the assignments
   *
   * @param entitlements
   *
   * @return {Observable<any>}
   */
  public confirmAssignments(entitlements: any): Observable<any> {
    const requestBody = {
      'claimRequests': []
    };

    let passesAndTickets = {
      'guest_type': Constants.GUEST_PRIMARY,
      'entitlementLinkingRequests': []
    };

    let shared = {
      'guest_type': Constants.GUEST_SHARED,
      'entitlementLinkingRequests': []
    };

    for (const entitlement of entitlements) {
      if (entitlement.linkEntitlementToGuestAccount) {
        passesAndTickets = this._generatePassesAndTicketsObject(passesAndTickets, entitlement);
      } else {
        shared = this._generateSharedEntitlementsObject(shared, entitlement['entitlementId']);
      }
    }

    if (passesAndTickets.entitlementLinkingRequests.length > 0) {
      requestBody.claimRequests.push(passesAndTickets);
    }

    if (shared.entitlementLinkingRequests.length > 0) {
      requestBody.claimRequests.push(shared);
    }

    return this.setRequestHeaders(this._getRequestHeaders())
      .setRequestBody(requestBody)
      .executeRequest(
        Constants.REQUEST_TYPE_POST,
        `${environment.apiConfiguration.basePath}${environment.apiConfiguration.paths.link}`
      ).pipe(
        catchError(this._handleTmsApiErrors.bind(this))
      );
  }

  /**
   * @desc get request headers to be used for current api calls
   * @returns
   */
  private _getRequestHeaders() {
    const enableProfileAuthenticator = (<any>this._configService.getConfig())?.enableProfileAuthenticator;
    let requestHeaders = <any>{
      'Accept-Language': this._localeHandler.getCurrentLocale()
    };

    if (enableProfileAuthenticator) {
      requestHeaders.swid = (<any>this._profileServce.getProfile())?.swid || '';
    }

    return requestHeaders;
  }

  /**
   * Use TMS Error Subject to clear general error message
   */
  public clearTmsApiGeneralError() {
    this.generalTmsErrorSubject.next(null);
  }

  /**
   * Generates Shared tickets request Object
   *
   * @param sharedObj
   * @param entitlementId
   *
   * @returns {any}
   */
  private _generateSharedEntitlementsObject(sharedObj: Object, entitlementId: number): any {
    sharedObj['entitlementLinkingRequests'].push({
      'visualId': entitlementId
    });

    return sharedObj;
  }

  /**
   * Generate Passes and Tickets request object
   *
   * @param passesAndTickets
   * @param entitlement
   *
   * returns {any}
   */
  private _generatePassesAndTicketsObject(passesAndTickets: Object, entitlement: Object): any {
    if (entitlement['type'] !== Constants.ENTITLEMENT_PASS) {
      passesAndTickets['entitlementLinkingRequests'].push({
        'visualId': entitlement['entitlementId'],
        'name': {
          'firstName': entitlement['assignedTo'].firstName.trim(),
          'lastName': entitlement['assignedTo'].lastName.trim()
        }
      });
    } else {
      passesAndTickets['entitlementLinkingRequests'].push({
        'visualId': entitlement['entitlementId']
      });
    }

    return passesAndTickets;
  }

  /**
   * Handle unexpected errors returned by TMS API
   *
   * @param errorResponse
   *
   * @returns {Observable<never>}
   */
  private _handleTmsApiErrors(errorResponse: HttpErrorResponse): Observable<never> {
    this.generalTmsErrorSubject.next(Constants.GENERIC_ERROR);

    return throwError(errorResponse);
  }
}
